import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Bell, 
  Plus, 
  Trash2, 
  Check,
  CalendarIcon,
  Clock,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { createReminder, getReminders, completeReminder, deleteReminder } from '@/services/api';
import { toast } from 'sonner';

const reminderTypes = [
  { value: 'quarterly_checkup', label: 'Quarterly Checkup' },
  { value: 'medication', label: 'Medication Reminder' },
  { value: 'appointment', label: 'Doctor Appointment' },
  { value: 'test', label: 'Lab Test' },
  { value: 'vaccination', label: 'Vaccination' },
  { value: 'other', label: 'Other' }
];

const reminderTypeColors = {
  quarterly_checkup: 'bg-blue-100 text-blue-700',
  medication: 'bg-purple-100 text-purple-700',
  appointment: 'bg-green-100 text-green-700',
  test: 'bg-amber-100 text-amber-700',
  vaccination: 'bg-pink-100 text-pink-700',
  other: 'bg-slate-100 text-slate-700'
};

export default function RemindersPage() {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Form states
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reminderDate, setReminderDate] = useState(null);
  const [reminderType, setReminderType] = useState('quarterly_checkup');

  useEffect(() => {
    loadReminders();
  }, []);

  const loadReminders = async () => {
    try {
      const data = await getReminders();
      setReminders(data);
    } catch (error) {
      console.error('Error loading reminders:', error);
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!reminderDate) {
      toast.error('Please select a date');
      return;
    }

    setCreating(true);
    try {
      await createReminder(title, description, format(reminderDate, 'yyyy-MM-dd'), reminderType);
      toast.success('Reminder created successfully!');
      setDialogOpen(false);
      resetForm();
      await loadReminders();
    } catch (error) {
      console.error('Create reminder error:', error);
      toast.error('Failed to create reminder');
    } finally {
      setCreating(false);
    }
  };

  const handleComplete = async (reminderId) => {
    try {
      await completeReminder(reminderId);
      setReminders(reminders.map(r => 
        r.id === reminderId ? { ...r, is_completed: true } : r
      ));
      toast.success('Reminder marked as complete');
    } catch (error) {
      console.error('Complete reminder error:', error);
      toast.error('Failed to complete reminder');
    }
  };

  const handleDelete = async (reminderId) => {
    if (!window.confirm('Are you sure you want to delete this reminder?')) return;

    try {
      await deleteReminder(reminderId);
      setReminders(reminders.filter(r => r.id !== reminderId));
      toast.success('Reminder deleted');
    } catch (error) {
      console.error('Delete reminder error:', error);
      toast.error('Failed to delete reminder');
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setReminderDate(null);
    setReminderType('quarterly_checkup');
  };

  const pendingReminders = reminders.filter(r => !r.is_completed);
  const completedReminders = reminders.filter(r => r.is_completed);

  const isOverdue = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="reminders-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Health Reminders
            </h1>
            <p className="text-slate-600 mt-1">Never miss your quarterly checkups and appointments</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg px-6 btn-active" data-testid="add-reminder-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Reminder
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Create Reminder</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Quarterly Blood Test"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    data-testid="reminder-title-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="type">Reminder Type</Label>
                  <Select value={reminderType} onValueChange={setReminderType}>
                    <SelectTrigger id="type" data-testid="reminder-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {reminderTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="reminder-date-trigger"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {reminderDate ? format(reminderDate, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={reminderDate}
                        onSelect={setReminderDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Textarea
                    id="description"
                    placeholder="Add any notes..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    data-testid="reminder-description-input"
                  />
                </div>

                <Button
                  className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                  onClick={handleCreate}
                  disabled={creating}
                  data-testid="create-reminder-btn"
                >
                  {creating ? 'Creating...' : 'Create Reminder'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton h-20 rounded-xl" />
            ))}
          </div>
        ) : (
          <>
            {/* Pending Reminders */}
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Upcoming ({pendingReminders.length})
              </h2>
              {pendingReminders.length === 0 ? (
                <Card className="bg-white border-slate-200 shadow-sm">
                  <CardContent className="py-8 text-center">
                    <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No upcoming reminders</p>
                    <p className="text-sm text-slate-400 mt-1">Add a reminder to stay on top of your health</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {pendingReminders.map((reminder, index) => {
                    const overdue = isOverdue(reminder.reminder_date);
                    return (
                      <Card 
                        key={reminder.id} 
                        className={`bg-white border shadow-sm card-hover animate-fade-in ${overdue ? 'border-red-200' : 'border-slate-200'}`}
                        style={{ animationDelay: `${index * 0.05}s`, opacity: 0 }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-4 min-w-0 flex-1">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${overdue ? 'bg-red-100' : 'bg-amber-100'}`}>
                                {overdue ? (
                                  <AlertCircle className="w-6 h-6 text-red-600" />
                                ) : (
                                  <Clock className="w-6 h-6 text-amber-700" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="font-semibold text-slate-900">{reminder.title}</h3>
                                <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                                  <span className={overdue ? 'text-red-600 font-medium' : ''}>
                                    {new Date(reminder.reminder_date).toLocaleDateString()}
                                    {overdue && ' (Overdue)'}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reminderTypeColors[reminder.reminder_type] || reminderTypeColors.other}`}>
                                    {reminderTypes.find(t => t.value === reminder.reminder_type)?.label || reminder.reminder_type}
                                  </span>
                                </div>
                                {reminder.description && (
                                  <p className="text-sm text-slate-500 mt-1 truncate">{reminder.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleComplete(reminder.id)}
                                className="text-green-600 border-green-200 hover:bg-green-50"
                                data-testid={`complete-reminder-${reminder.id}`}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Done
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(reminder.id)}
                                className="text-slate-400 hover:text-red-600"
                                data-testid={`delete-reminder-${reminder.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Completed Reminders */}
            {completedReminders.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Completed ({completedReminders.length})
                </h2>
                <div className="space-y-3">
                  {completedReminders.slice(0, 5).map((reminder, index) => (
                    <Card 
                      key={reminder.id} 
                      className="bg-slate-50 border-slate-200 shadow-sm animate-fade-in"
                      style={{ animationDelay: `${index * 0.05}s`, opacity: 0 }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-4 min-w-0 flex-1">
                            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Check className="w-5 h-5 text-green-600" />
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-medium text-slate-500 line-through">{reminder.title}</h3>
                              <p className="text-sm text-slate-400">{new Date(reminder.reminder_date).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(reminder.id)}
                            className="text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
